import { useRef, useState } from 'react';

import api from '../../utils/api';
import useAutocomplete from '../../hooks/useAutocomplete';
import { uploadPhotos } from '../../utils/uploadPhotos';
import { uploadAudioNotes } from '../../utils/audioNotes';
import { captureDeviceGps } from '../../utils/geo';
import {
  collectionEditRecord, isUnreachable, newCollectionRecord, newUuid, vegetationRecords,
} from '../../utils/collectionRecord';
import {
  EMPTY_NEW, canAdvance as canAdvance_, toLocalDatetime,
} from './wizardRules';

/**
 * Everything the new-collection wizard is and does.
 *
 * Six steps' worth of state, three search boxes, and the two ways a record
 * leaves this screen: to the server, or -- when nothing answers -- to this
 * device's sync queue.
 *
 * The distinction between those two is the most important line in the file,
 * and it is drawn in submit()'s catch. A failure with no response means
 * nothing answered, and only then is the queue the right answer. If the
 * server DID answer -- 400 for a latitude of 200, 403 for editing somebody
 * else's draft, 429 for the daily public limit -- that is a considered
 * refusal from the endpoint that does the considering. /sync upserts the row
 * it is handed and checks none of it, so queueing a refused request would
 * turn "no" into "saved" by going round the back.
 *
 * What it needs from the page: `t` and `user` to write the record and the
 * notifications, `enqueue` for the offline path, `reload` to refresh the
 * table, and the two notice setters, because a queued save and a photograph
 * that did not upload are both reported outside the wizard and outlive it.
 *
 * What it returns is shaped for CollectionWizard: a field bundle per search
 * box, plus the form, the step and the two ways out.
 */
export default function useCollectionWizard({
  t, user, enqueue, reload, setQueuedNotice, setPhotoWarn,
}) {
  // Wizard
  const [wizardStep, setWizardStep]       = useState(0);
  const [newForm, setNewForm]             = useState(EMPTY_NEW);
  const [newVeg, setNewVeg]               = useState([]);
  const [resumeSampleId, setResumeSampleId] = useState(null);
  const [resumeExistingVeg, setResumeExistingVeg] = useState([]);
  const [existingPhotoTypes, setExistingPhotoTypes] = useState(() => new Set());
  const [resumeError, setResumeError]     = useState('');
  const setColourChange = (val) => setNewForm(f => ({ ...f, colour_change: val }));
  const [stepPhotos, setStepPhotos]       = useState({});
  const [vegUnknownPhoto, setVegUnknownPhoto] = useState(null);
  // The vegetation-species box in wizard step 2 (hooks/useAutocomplete.js).
  // Picking one ADDS to the list rather than replacing a value, so the box
  // empties itself afterwards, ready for the next plant.
  const veg = useAutocomplete({
    search: term => api.get(`/vegetation-species/search?q=${encodeURIComponent(term)}`)
      .then(res => res.data || []),
    onChoose: sp => setNewVeg(prev => [...prev, {
      species: sp.scientific_name, is_dominant: false,
      vegetation_species_id: sp.species_id, hebrew_name: sp.hebrew_name,
      common_name_en: sp.common_name_en,
    }]),
  });
  // Add-new-species mini-form (wizard step 2, CM/IE only)
  const [showAddVegForm, setShowAddVegForm] = useState(false);
  const vegLookupSeqRef = useRef(0);
  const [supplementaryList, setSupplementaryList] = useState([]);
  const [supplementaryInput, setSupplementaryInput] = useState('');
  const [otherPhotoNote, setOtherPhotoNote]   = useState('');
  // Step 5 "more photos": any number, chosen together, each with its own type.
  const [extraPhotos, setExtraPhotos]         = useState([]);
  // Step 6's voice notes (VoiceNoteRecorder), sent once the collection exists.
  const [audioNotes, setAudioNotes]           = useState([]);
  const [newError, setNewError]           = useState('');
  const [newSaving, setNewSaving]         = useState(false);
  const [draftSaved, setDraftSaved]       = useState(false);

  // Taxon (wizard step 4) and region (step 2), both on
  // hooks/useAutocomplete.js. `taxonSelected` and `regionSelected` stay here:
  // they are not the box's business but the wizard's, because step 4 will not
  // advance on a TYPED name that was never chosen from the dictionary, and
  // step 2 counts a chosen region as knowing where the specimen came from.
  const [taxonSelected, setTaxonSelected] = useState(false);
  const [regionSelected, setRegionSelected] = useState(false);
  const lastConfirmedRegionRef = useRef({ query: '', value: '' });
  const regionLookupSeqRef = useRef(0);

  const newFormRef = useRef(null);
  newFormRef.current = newForm;
  const taxon = useAutocomplete({
    // The rank narrows the search, and it is read at lookup time rather than
    // captured: somebody may pick the rank after starting to type the name.
    search: (term) => {
      const rank = newFormRef.current?.current_rank;
      const rankParam = rank ? `&rank=${encodeURIComponent(rank)}` : '';
      return api.get(`/taxonomy/search?q=${encodeURIComponent(term)}${rankParam}`)
        .then(res => res.data || []);
    },
    onType: () => { setTaxonSelected(false); setNewForm(f => ({ ...f, current_taxon_name: '' })); },
    onChoose: (name) => { setTaxonSelected(true); setNewForm(f => ({ ...f, current_taxon_name: name })); },
  });

  const region = useAutocomplete({
    search: term => api.get(`/regions/search?q=${encodeURIComponent(term)}`)
      .then(res => res.data || []),
    onType: () => { setRegionSelected(false); setNewForm(f => ({ ...f, region: '' })); },
    onChoose: (loc) => {
      const value = loc.name_en || loc.name_he;
      setRegionSelected(true);
      setNewForm(f => ({ ...f, region: value }));
      lastConfirmedRegionRef.current = { query: loc.name_he, value };
      regionLookupSeqRef.current++;   // no in-flight add-region may undo this
    },
  });

  // Add-new-region mini-form (wizard step 2, CM/IE only)
  const [showAddRegionForm, setShowAddRegionForm] = useState(false);

  // ---- Wizard helpers ----

  const openWizard = () => {
    setAudioNotes([]);
    setNewForm({ ...EMPTY_NEW, collection_datetime: toLocalDatetime() });
    setNewVeg([]);
    setResumeSampleId(null);
    setResumeExistingVeg([]);
    setExistingPhotoTypes(new Set());
    setResumeError('');
    setStepPhotos({});
    setVegUnknownPhoto(null);
    resetVegAutocomplete();
    setSupplementaryList([]);
    setSupplementaryInput('');
    setOtherPhotoNote(''); setExtraPhotos([]);
    setNewError('');
    setWizardStep(1);
    resetTaxonAutocomplete();
    resetRegionAutocomplete();
    captureDeviceGps()
      .then(({ lat, lng }) => setNewForm(f => ({ ...f, gps_lat: lat, gps_lng: lng })))
      .catch(() => {});
  };

  const resetTaxonAutocomplete = () => {
    taxon.reset();
    setTaxonSelected(false);
  };

  const resetRegionAutocomplete = () => {
    region.reset();
    setRegionSelected(false);
    lastConfirmedRegionRef.current = { query: '', value: '' };
    setShowAddRegionForm(false);   // unmounts it, which is what clears its fields
    regionLookupSeqRef.current++; // invalidate any in-flight add-region POST from before this reset
  };

  const resetVegAutocomplete = () => {
    veg.reset();
    setShowAddVegForm(false);
    vegLookupSeqRef.current++;
  };


  // Reopens the wizard for an existing draft, pre-filled with whatever was
  // already saved, so the owning collector can add whatever's still missing
  // and hit Submit again — instead of the record staying an orphaned draft.
  const resumeDraft = (sampleId) => {
    setResumeError('');
    Promise.all([
      api.get(`/collections/${sampleId}`),
      api.get(`/collections/${sampleId}/vegetation`),
      api.get(`/collections/${sampleId}/photos`),
    ]).then(([collRes, vegRes, photosRes]) => {
      const d = collRes.data;
      setNewForm({
        qr_uid:              d.qr_uid || '',
        collection_datetime: d.collection_datetime ? toLocalDatetime(new Date(d.collection_datetime)) : toLocalDatetime(),
        substrate:           d.substrate || '',
        fruiting_body_size:  d.fruiting_body_size || '',
        phenological_stage:  d.phenological_stage || '',
        fruiting_body_count: d.fruiting_body_count ?? '',
        growth_pattern:      d.growth_pattern || '',
        fruiting_body_size_estimated: d.fruiting_body_size_estimated ?? false,
        gps_lat:             d.gps_lat || '',
        gps_lng:             d.gps_lng || '',
        current_taxon_name:  d.current_taxon_name || '',
        current_rank:        d.current_rank || '',
        current_confidence:  d.current_confidence || '',
        general_notes:       d.general_notes || '',
        region:              d.region || '',
        colour_change:       d.colour_change ?? null,
      });
      const veg = vegRes.data.map(v => ({ species: v.species, is_dominant: v.is_dominant, vegetation_species_id: v.vegetation_species_id, hebrew_name: v.hebrew_name }));
      setNewVeg(veg);
      setResumeExistingVeg(veg.map(v => v.species));
      setExistingPhotoTypes(new Set(photosRes.data.map(p => p.photo_type)));
      setStepPhotos({});
      setVegUnknownPhoto(null);
      setSupplementaryList([]);
      setSupplementaryInput('');
      setOtherPhotoNote(''); setExtraPhotos([]);
      resetTaxonAutocomplete();
      if (d.current_taxon_name) {
        taxon.reset(d.current_taxon_name);
        setTaxonSelected(true);
      }
      resetRegionAutocomplete();
      if (d.region) {
        region.reset(d.region);
        setRegionSelected(true);
        lastConfirmedRegionRef.current = { query: d.region, value: d.region };
      }
      setDraftSaved(false);
      setNewError('');
      setResumeSampleId(sampleId);
      setWizardStep(1);
    }).catch(() => {
      setResumeError(t('wizard.resumeFailed'));
    });
  };

  const closeWizard = () => {
    setWizardStep(0);
    setAudioNotes([]);
    setNewForm(EMPTY_NEW);
    setNewVeg([]);
    setResumeSampleId(null);
    setResumeExistingVeg([]);
    setExistingPhotoTypes(new Set());
    setResumeError('');
    setStepPhotos({});
    setVegUnknownPhoto(null);
    resetVegAutocomplete();
    setSupplementaryList([]);
    setSupplementaryInput('');
    setOtherPhotoNote(''); setExtraPhotos([]);
    setNewError('');
    setDraftSaved(false);
    resetTaxonAutocomplete();
    resetRegionAutocomplete();
  };

  // collections/wizardRules.js, where each rule is a rule about what makes a
  // collection worth keeping once nobody can go back and look again.
  const canAdvance = step => canAdvance_(step, {
    form: newForm, vegetation: newVeg, regionSelected,
    taxonQuery: taxon.query, taxonSelected,
    photos: stepPhotos, otherPhotoNote,
  });

  const handleNewChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleVegQueryChange = veg.onChange;
  const selectVegSpecies = sp => veg.choose(sp);

  // A plant the dictionary does not have. Recorded by name so the record is
  // not poorer for it; an editor may add it to the dictionary properly later.
  const addVegFreeText = () => {
    const name = veg.query.trim();
    if (!name) return;
    setNewVeg(prev => [...prev, { species: name, is_dominant: false, vegetation_species_id: null }]);
    veg.reset();
  };

  const addUnknownVeg = () => {
    setNewVeg(prev => [...prev, { species: 'unknown', is_dominant: false, identification_type: null }]);
  };

  const removeVegEntry = (index) => {
    setNewVeg(prev => prev.filter((_, i) => i !== index));
  };

  const toggleVegDominant = (index) => {
    setNewVeg(prev => prev.map((v, i) => i === index ? { ...v, is_dominant: !v.is_dominant } : v));
  };

  const setPhotoFile = (photoType, file) => {
    setStepPhotos(prev => {
      if (file === null) { const n = { ...prev }; delete n[photoType]; return n; }
      return { ...prev, [photoType]: file };
    });
  };

  /*
   * Nothing answered, so the work goes to this device's queue instead.
   *
   * Reached only from the `isUnreachable` branch in submitNew. Two things it
   * cannot do, and both are said out loud rather than papered over:
   *
   *   - Photos. /sync moves table rows; an image is a multipart upload into a
   *     content-addressed store. Queueing a `photos` row would point the bank
   *     at a file that exists nowhere, which is worse than not queueing it.
   *   - Submitting. submit_collection counts the photos and vegetation on the
   *     server before it will clear is_draft. A device cannot vouch for that,
   *     so an offline Submit saves a draft -- and the notice says so, because
   *     a collector who thinks they submitted will not come back to finish it.
   */
  const queueCollection = async ({ isDraft, dtISO, photoCount }) => {
    const sampleId = resumeSampleId || newUuid();

    if (resumeSampleId) {
      // Already on the server: this is an edit, and must not carry is_draft or
      // collector_id back over whatever the record has become since.
      await enqueue('field_collection', collectionEditRecord(sampleId, newForm));
    } else {
      await enqueue('field_collection', newCollectionRecord(
        { ...newForm, collection_datetime: dtISO },
        { collectorId: user?.user_id, sampleId },
      ));
    }

    const veg = [
      ...newVeg.filter(v => !resumeExistingVeg.includes(v.species)),
      ...supplementaryList.map(species => ({ species, is_dominant: false })),
    ];
    for (const record of vegetationRecords(veg, { sampleId })) {
      await enqueue('sample_vegetation', record);
    }

    setNewError('');
    setQueuedNotice(isDraft ? t('wizard.queuedDraft') : t('wizard.queuedAsDraft'));
    // A voice note, like a photograph, is a file /sync cannot carry.
    const unsent = [photoCount > 0 ? t('wizard.queuedPhotosNotSent', { count: photoCount }) : '',
      audioNotes.length > 0 ? t('voice:queuedNotSent', { count: audioNotes.length }) : ''].filter(Boolean);
    if (unsent.length) setPhotoWarn(unsent.join(' '));
    closeWizard();
  };

  const submitNew = async (isDraft) => {
    setNewError('');
    setQueuedNotice('');
    if (!isDraft) {
      for (let s = 1; s <= 5; s++) {
        if (!canAdvance(s)) {
          setWizardStep(s);
          setNewError(t('wizard.submitValidationError'));
          return;
        }
      }
    }
    setNewSaving(true);
    const dtISO = newForm.collection_datetime
      ? new Date(newForm.collection_datetime).toISOString()
      : null;
    try {
      let sampleId = resumeSampleId;
      if (sampleId) {
        await api.put(`/collections/${sampleId}`, { ...newForm });
      } else {
        const res = await api.post('/collections', { ...newForm, collection_datetime: dtISO });
        sampleId = res.data.sample_id;
      }

      const vegFailed = [];
      for (const veg of newVeg) {
        if (resumeExistingVeg.includes(veg.species)) continue; // already saved in an earlier session
        try {
          await api.post(`/collections/${sampleId}/vegetation`, veg);
        } catch (err) {
          console.error('Vegetation save failed for', veg.species, err);
          vegFailed.push(veg.species);
        }
      }
      for (const item of supplementaryList) {
        try {
          await api.post(`/collections/${sampleId}/vegetation`, { species: item, is_dominant: false });
        } catch (err) {
          console.error('Supplementary item save failed for', item, err);
          vegFailed.push(item);
        }
      }
      if (vegFailed.length > 0) {
        setNewError(t('wizard.vegSaveFailed', { count: vegFailed.length, species: vegFailed.join(', ') }));
      }

      const photosArray = Object.entries(stepPhotos).map(([photoType, file]) => ({
        file, photoType, previewUrl: URL.createObjectURL(file),
        ...(photoType === 'other' ? { notes: otherPhotoNote.trim() } : {}),
      }));
      if (vegUnknownPhoto) {
        photosArray.push({ file: vegUnknownPhoto, photoType: 'other', previewUrl: URL.createObjectURL(vegUnknownPhoto) });
      }
      for (const extra of extraPhotos) {
        photosArray.push({ file: extra.file, photoType: extra.photoType, previewUrl: extra.previewUrl });
      }
      const fileWarnings = [];
      if (photosArray.length > 0) {
        const uploadResult = await uploadPhotos(photosArray, { sample_id: sampleId }, newForm.current_taxon_name, 'field_collection');
        if (uploadResult.failed.length > 0) {
          const names = uploadResult.failed.map(f => f.photoType.replace(/_/g, ' ')).join(', ');
          fileWarnings.push(t('wizard.photoUploadFailed', { count: uploadResult.failed.length, names }));
        }
      }
      // The voice notes, once the record exists. One that went is dropped
      // from the form, so a later Submit does not send it twice.
      if (audioNotes.length > 0) {
        const { sent, failed } = await uploadAudioNotes(audioNotes, { sample_id: sampleId });
        setAudioNotes(list => list.filter(n => !sent.includes(n.id)));
        if (failed.length > 0) fileWarnings.push(t('voice:notSent', { count: failed.length }));
      }
      if (fileWarnings.length > 0) setPhotoWarn(fileWarnings.join(' '));

      if (!isDraft) {
        try {
          await api.put(`/collections/${sampleId}/submit`);
        } catch (err) {
          const missing = err.response?.data?.missing;
          setNewError(missing?.length
            ? t('wizard.submitMissingRequirements', { list: missing.join(', ') })
            : (err.response?.data?.error || t('wizard.saveFailed')));
          reload();
          return;
        }
      }

      reload();
      if (isDraft) {
        setDraftSaved(true);
      } else {
        closeWizard();
      }
    } catch (err) {
      /*
       * A failure with no response means nothing answered, and only then is
       * the queue the right answer.
       *
       * If the server DID answer -- 400 for a latitude of 200, 403 for editing
       * somebody else's draft, 429 for the daily public limit -- that is a
       * considered refusal from the endpoint that does the considering. /sync
       * upserts the row it is handed and checks none of it, so queueing a
       * refused request would turn "no" into "saved" by going round the back.
       * Those are shown as the errors they are.
       */
      if (isUnreachable(err)) {
        const photoCount = Object.keys(stepPhotos).length
          + (vegUnknownPhoto ? 1 : 0) + extraPhotos.length;
        await queueCollection({ isDraft, dtISO, photoCount });
      } else {
        setNewError(err.response?.data?.error || t('wizard.saveFailed'));
      }
    } finally {
      setNewSaving(false);
    }
  };

  const handleTaxonSelect = name => taxon.choose(name, { text: name });
  const handleRegionInput = region.onChange;
  const handleRegionSelect = loc => region.choose(loc, { text: loc.name_he });

  // What step 2 needs around each of its two search boxes: whether a choice
  // was made, the counter a stale lookup is checked against, and whether the
  // add-to-dictionary form is open.
  const regionField = {
    box: region, selected: regionSelected, setSelected: setRegionSelected,
    lastConfirmedRef: lastConfirmedRegionRef, seqRef: regionLookupSeqRef,
    onInput: handleRegionInput, onPick: handleRegionSelect,
    adding: showAddRegionForm, setAdding: setShowAddRegionForm,
  };

  const vegField = {
    box: veg, list: newVeg, seqRef: vegLookupSeqRef,
    onInput: handleVegQueryChange, onPick: selectVegSpecies,
    onFreeText: addVegFreeText, onUnknown: addUnknownVeg,
    onToggleDominant: toggleVegDominant, onRemove: removeVegEntry,
    adding: showAddVegForm, setAdding: setShowAddVegForm,
  };

  const selectUnknownTaxon = () => {
    setNewForm(f => ({ ...f, current_rank: 'unknown' }));
    handleTaxonSelect('Unknown');
  };

  const handleRankChange = (e) => {
    const rank = e.target.value;
    // Clear any taxon selection made under a different rank so stale data can't submit
    setNewForm(f => ({ ...f, current_rank: rank, current_taxon_name: '' }));
    taxon.reset();
    setTaxonSelected(false);
  };

  // The rest of what the wizard keeps, bundled the same way as regionField
  // and vegField above.
  const taxonField = {
    box: taxon, selected: taxonSelected, onPick: handleTaxonSelect,
    onRankChange: handleRankChange, onMarkUnknown: selectUnknownTaxon,
  };

  const photoField = {
    slots: stepPhotos, setPhoto: setPhotoFile, existingTypes: existingPhotoTypes,
    extra: extraPhotos, setExtra: setExtraPhotos,
    otherNote: otherPhotoNote, setOtherNote: setOtherPhotoNote,
    vegUnknown: vegUnknownPhoto, setVegUnknown: setVegUnknownPhoto,
    setColourChange,
    // Step 6's voice notes.
    audio: audioNotes, setAudio: setAudioNotes,
  };

  const suppField = {
    list: supplementaryList, setList: setSupplementaryList,
    input: supplementaryInput, setInput: setSupplementaryInput,
  };
  return {
    // the wizard frame
    step: wizardStep, setStep: setWizardStep, canAdvance,
    open: openWizard, close: closeWizard, resume: resumeDraft, resumeError,
    // the record being built
    form: newForm, setForm: setNewForm, onFormChange: handleNewChange,
    vegetation: newVeg,
    // one bundle per search box, plus the photographs and the extra plants
    regionField, vegField, taxonField, photoField, suppField,
    // saving
    error: newError, setError: setNewError, saving: newSaving,
    draftSaved, submit: submitNew,
  };
}
